/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { SHIFTIQ_ENABLED, NAV_HIDDEN_PAGE_IDS } from "../../features";
import { API_BASE, SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { useStaff } from "../../hooks";
import { Icon, LoadingPulse } from "../../ui";
import { useIsMobile } from "../../hooks/useIsMobile";
import { schedulingGroupFor, ROLES, PLATFORM_USER_ROLE_OPTS, isKitchenStaffL } from "../../lib/roles";
import { staffMatchesSearch, userEmployeeCode } from "../../lib/userDisplay";
import { defaultPhysicalStoreForRole } from "../../theme";
import { brandSelectOptions, defaultBrandId, isKnownBrandId } from "../../storeBrand";
import { locationIdForStore, locationScopeQuery, storeNameForLocationId } from "../../lib/storeScope";
import { leaveRequestMeta, fetchStaffLeaveBalance, STORE_DEFAULT_HOURS } from "../../lib/leave";
import { adminQueryFromSearch, searchForAdmin, replaceBrowserPath } from "../../routing/paths";
import { combineSignals, pageSignal } from "../../routing/pageAbort";

import { HeadcountPlanningPage } from "./HeadcountPlanningPage";
import { NotificationBellIndicator } from "../settings/NotificationSettings";
export function AdminPage({ user, lang, allUsers, setAllUsers }) {
  const role = String(user.role || "").trim().toLowerCase();
  const isFullAdmin = role === "admin";
  const isBrandManager = role === "brand_manager";
  const isOpsManager = role === "ops_manager";
  const isAdminOrBrandManager = isFullAdmin || isBrandManager;
  /** Admin panel: T2+ and HR (matches App nav). Opening hours editor uses the same bar. */
  const canAdmin = isAdminOrBrandManager || isOpsManager || role === "hr";
  const canEditStoreSchedule = canAdmin;
  const { storeNames, storesWithAll, brands, storeRecords } = usePgStores();
  const brandOptions = brandSelectOptions(brands);
  const pickDefaultStore = () => defaultPhysicalStoreForRole(storeNames, user.role);
  const pickDefaultBrandId = () => defaultBrandId(brands);

  const initialAdminQuery = useRef(adminQueryFromSearch(
    typeof window !== "undefined" ? window.location.search : ""
  )).current;

  const [tab, setTab]             = useState(initialAdminQuery.tab);
  const [search, setSearch]       = useState("");
  const [filterStore, setFilter]  = useState("All Stores");
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [rejectLeaveModal, setRejectLeaveModal] = useState(null);
  const isMobile = useIsMobile();
  const { staff, loading: staffLoading, error: staffError } = useStaff(undefined, { allowUnscoped: true });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [toast, setToast] = useState(null);
  const [savingUser, setSavingUser] = useState(false);

  const emptyNew = useMemo(
    () => ({
    name: "",
    email: "",
    password: "",
    role: "store_manager",
    store: defaultPhysicalStoreForRole(storeNames, user.role),
    brand_id: null,
    job_title: "",
    group_name: "supervisor",
    shift_hours: 8,
    work_days: 5,
    active: true,
    }),
    [storeNames, user.role]
  );
  const [newEmp, setNewEmp] = useState(emptyNew);
  const [editEmp, setEditEmp] = useState(null);
  const [addError, setAddError] = useState("");

  const LEAVE_REQS_MOCK = [
    { id:1, employeeId:1, type:"3012",      date:"2026-04-10", reason:"Personal appointment", status:"pending", createdAt:"2026-03-29", overdue:true },
    { id:2, employeeId:2, type:"rest_day",   date:"2026-04-14", reason:"",                     status:"pending", createdAt:"2026-03-30" },
    { id:3, employeeId:5, type:"shift_swap", date:"2026-04-12", reason:"Doctor appointment",   status:"pending", createdAt:"2026-03-31" },
    { id:4, employeeId:6, type:"shift_preference", date:"2026-04-15", reason:"", status:"pending", createdAt:"2026-04-01", shift_preference:"Morning shift on Wednesday" },
  ];
  const [leaveReqs, setLeaveReqs] = useState(LEAVE_REQS_MOCK);
  const [hoursStore, setHoursStore] = useState(() => defaultPhysicalStoreForRole(storeNames, user.role));
  const [storeHours, setStoreHours] = useState(null);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [leaveEdits, setLeaveEdits] = useState({});
  const leaveConfigDebounceRef = useRef({});
  const [adminLeaveBalancesByCode, setAdminLeaveBalancesByCode] = useState({});
  const [adminBalancesLoading, setAdminBalancesLoading] = useState(false);
  const leaveEditsDebounceKey = useMemo(() => JSON.stringify(leaveEdits), [leaveEdits]);
  const employees = staff ?? [];
  const platformUsers = users;
  const groupLabel = group => ({
    service: "Service Crew",
    kitchen: "Kitchen Staff",
    supervisor: "Store Manager / Supervisor",
    cleaning: "Cleaning Crew",
  }[group] || "Service Crew");
  const usersAbortRef = useRef(null);
  const loadUsers = useCallback(() => {
    usersAbortRef.current?.abort();
    const local = new AbortController();
    usersAbortRef.current = local;
    const signal = combineSignals(local.signal, pageSignal());
    setUsersLoading(true);
    setUsersError("");
    fetch(`${API_BASE}/users`, { headers: getAuthHeaders(), signal })
      .then(async (r) => {
        if (!r.ok) throw new Error("users_fetch_failed");
        return r.json();
      })
      .then((rows) => {
        if (!signal.aborted) setUsers(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return;
        setUsersError(lang === "gr" ? "Σφάλμα φόρτωσης χρηστών" : "Could not load users");
      })
      .finally(() => {
        if (!signal.aborted) setUsersLoading(false);
      });
  }, [lang]);

  useEffect(() => {
    loadUsers();
    return () => usersAbortRef.current?.abort();
  }, [loadUsers]);

  useEffect(() => {
    if (initialAdminQuery.allStores) {
      setFilter("All Stores");
    } else {
      const fromId = storeNameForLocationId(initialAdminQuery.locationId, storeRecords);
      const fromName = storeNames.includes(initialAdminQuery.storeName) ? initialAdminQuery.storeName : "";
      const preferred = fromId || fromName;
      if (preferred) setFilter(preferred);
    }
    const fromId = storeNameForLocationId(initialAdminQuery.locationId, storeRecords);
    const fromName = storeNames.includes(initialAdminQuery.storeName) ? initialAdminQuery.storeName : "";
    const preferred = fromId || fromName;
    const d = defaultPhysicalStoreForRole(storeNames, user.role);
    setHoursStore((h) => (preferred && storeNames.includes(preferred) ? preferred : (storeNames.includes(h) ? h : d)));
  }, [storeNames, storeRecords, user.role]);

  useEffect(() => {
    const allStores = filterStore === "All Stores";
    const locationId = allStores ? null : locationIdForStore(filterStore, storeRecords);
    if (!allStores && !locationId && filterStore) return undefined;
    replaceBrowserPath("/admin", searchForAdmin({ locationId, allStores, tab }));
    return undefined;
  }, [filterStore, tab, storeRecords]);

  useEffect(() => {
    const hoursLocationId = locationIdForStore(hoursStore, storeRecords);
    if (!hoursLocationId) return;
    const local = new AbortController();
    const signal = combineSignals(local.signal, pageSignal());
    setStoreHours(null);
    fetch(`${SHIFTIQ_API}/opening-hours?${locationScopeQuery(hoursLocationId)}`, { headers: getAuthHeaders(), signal })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (signal.aborted) return;
        setStoreHours(
          data?.hours ?? STORE_DEFAULT_HOURS[hoursStore] ?? {}
        );
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return;
        setStoreHours(
          STORE_DEFAULT_HOURS[hoursStore] ?? {}
        );
      });
    return () => local.abort();
  }, [hoursStore, storeRecords]);

  useEffect(() => {
    if (tab !== "leave") return undefined;
    const codes = [
      ...new Set(
        employees
          .map((emp) => emp?.employee_code || userEmployeeCode(emp))
          .filter((code) => code && String(code).trim())
          .map((code) => String(code).trim())
      ),
    ];
    if (codes.length === 0) {
      setAdminLeaveBalancesByCode({});
      setAdminBalancesLoading(false);
      return undefined;
    }
    const local = new AbortController();
    const signal = combineSignals(local.signal, pageSignal());
    const delayMs = Object.keys(leaveEdits).length > 0 ? 900 : 0;
    const timer = setTimeout(() => {
      setAdminBalancesLoading(true);
      Promise.all(
        codes.map((code) =>
          fetchStaffLeaveBalance(code, { signal }).then((bal) => (bal ? [code, bal] : null))
        )
      )
        .then((pairs) => {
          if (signal.aborted) return;
          const next = {};
          pairs.filter(Boolean).forEach(([code, bal]) => {
            next[code] = bal;
          });
          setAdminLeaveBalancesByCode(next);
        })
        .catch((err) => {
          if (signal.aborted || isAbortError(err)) return;
          setAdminLeaveBalancesByCode({});
        })
        .finally(() => {
          if (!signal.aborted) setAdminBalancesLoading(false);
        });
    }, delayMs);
    return () => {
      local.abort();
      clearTimeout(timer);
    };
  }, [tab, employees, leaveEditsDebounceKey]);

  const editingEmp = editingId ? platformUsers.find(u=>u.id===editingId) : null;

  useEffect(() => {
    if (!editingEmp) {
      setEditEmp(null);
      return;
    }
    setEditEmp({
      ...editingEmp,
      store: (editingEmp.role === "admin" || editingEmp.role === "brand_manager") ? "All Stores" : editingEmp.store,
      brand_id: editingEmp.brand_id ?? null,
      shift_hours: editingEmp.shift_hours ?? editingEmp.shiftHours ?? 8,
      work_days: editingEmp.work_days ?? editingEmp.workDays ?? 5,
      group_name: editingEmp.group_name ?? editingEmp.group ?? "service",
      password: "",
    });
  }, [editingEmp]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  if (staffLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <LoadingPulse size={28} />
        <div style={{ fontSize: 13, color: "var(--text-muted)",
          fontWeight: 600, marginTop: 12 }}>
          {lang === "gr" ? "Φόρτωση προσωπικού..." :
           "Loading staff..."}
        </div>
      </div>
    );
  }
  if (staffError) {
    return <div style={{ textAlign:"center", padding: 48, color:"var(--danger)", fontSize: 14 }}>{lang==="gr"?"Σφάλμα φόρτωσης προσωπικού":"Could not load staff data. Is the API running?"}</div>;
  }
  if (!canAdmin) {
    return (
      <div className="empty-state"><Icon name="shield" size={40}/><p>{lang==="gr"?"Δεν έχετε πρόσβαση":"You don't have access to the admin panel"}</p></div>
    );
  }

  function mergeLeaveEmp(emp) {
    const code = emp?.employee_code;
    if (!code || !leaveEdits[code]) return emp;
    return { ...emp, ...leaveEdits[code] };
  }

  function queueLeaveConfigPatch(employeeCode, body) {
    const code = String(employeeCode || "").trim();
    if (!code) return;
    if (leaveConfigDebounceRef.current[code]) {
      clearTimeout(leaveConfigDebounceRef.current[code]);
    }
    leaveConfigDebounceRef.current[code] = setTimeout(() => {
      fetch(`${API_BASE}/staff/leave-config/${encodeURIComponent(code)}`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, updated_by: user.name }),
      }).catch(() => {
        setToast({
          kind: "err",
          message: lang === "gr" ? "Σφάλμα αποθήκευσης ρυθμίσεων άδειας" : "Could not save leave settings",
        });
      });
      delete leaveConfigDebounceRef.current[code];
    }, 800);
  }

  function updateLeaveConfig(emp, fields) {
    const code = emp?.employee_code;
    if (!code) return;
    const next = { ...fields };
    if (Object.prototype.hasOwnProperty.call(fields, "prior_years")) {
      next.prior_years_reviewed = true;
    }
    setLeaveEdits((prev) => ({ ...prev, [code]: { ...prev[code], ...next } }));
    queueLeaveConfigPatch(code, fields);
  }
  function addEmployee() {
    if (!newEmp.name.trim()) {
      setAddError(lang === "gr" ? "Το όνομα είναι υποχρεωτικό" : "Name is required");
      return;
    }
    if (!newEmp.email.includes("@")) {
      setAddError(lang === "gr" ? "Μη έγκυρο email" : "Invalid email address");
      return;
    }
    if (!newEmp.password.trim()) {
      setAddError(lang === "gr" ? "Ο κωδικός είναι υποχρεωτικός" : "Password is required");
      return;
    }
    if (newEmp.password.trim().length < 8) {
      setAddError(lang === "gr" ? "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες" : "Password must be at least 8 characters");
      return;
    }
    if (String(newEmp.email || "").trim().toLowerCase().endsWith("@stiq.gr")) {
      setAddError(
        lang === "gr"
          ? "Οι λογαριασμοί @stiq.gr προστίθενται από Identity (Google), όχι με κωδικό."
          : "@stiq.gr accounts are provisioned in Identity (Google), not with a password."
      );
      return;
    }
    if (newEmp.role === "brand_manager" && !isKnownBrandId(newEmp.brand_id, brands)) {
      setAddError(lang === "gr" ? "Επιλέξτε brand" : "Brand is required for Brand Manager");
      return;
    }
    setSavingUser(true);
    fetch(`${API_BASE}/users`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name: newEmp.name,
        email: newEmp.email,
        password: newEmp.password,
        role: newEmp.role,
        store: newEmp.role === "hr" ? (newEmp.store || "all") : (newEmp.role === "admin" || newEmp.role === "brand_manager") ? "All Stores" : newEmp.store,
        brand_id: newEmp.role === "brand_manager" ? Number(newEmp.brand_id) : null,
      }),
    })
      .then(async (r) => {
        if (r.status === 409) {
          setAddError(lang === "gr" ? "Το email χρησιμοποιείται ήδη" : "Email already in use");
          return null;
        }
        if (!r.ok) throw new Error("create_failed");
        return r.json();
      })
      .then((created) => {
        if (!created) return;
        setShowAdd(false);
        setNewEmp(emptyNew);
        setAddError("");
        setToast({ kind: "ok", message: lang === "gr" ? "Ο χρήστης δημιουργήθηκε" : "User created" });
        loadUsers();
      })
      .catch(() => setToast({ kind: "err", message: lang === "gr" ? "Σφάλμα δημιουργίας χρήστη" : "Error creating user" }))
      .finally(() => setSavingUser(false));
  }
  function saveEditUser() {
    if (!editEmp) return;
    if (editEmp.password && String(editEmp.password).trim() !== "" && String(editEmp.password).trim().length < 8) {
      setToast({ kind: "err", message: lang === "gr" ? "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες" : "Password must be at least 8 characters" });
      return;
    }
    const body = {
      name: editEmp.name,
      email: editEmp.email,
      role: editEmp.role,
      store: editEmp.role === "hr" ? (editEmp.store || "all") : (editEmp.role === "admin" || editEmp.role === "brand_manager") ? "All Stores" : editEmp.store,
      brand_id: editEmp.role === "brand_manager" ? Number(editEmp.brand_id) : null,
      active: editEmp.active !== false,
    };
    if (editEmp.password && String(editEmp.password).trim() !== "") body.password = editEmp.password;
    setSavingUser(true);
    fetch(`${API_BASE}/users/${editEmp.id}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(() => {
        setEditingId(null);
        setEditEmp(null);
        setDeleteConfirm(false);
        setToast({ kind: "ok", message: lang === "gr" ? "Ο χρήστης ενημερώθηκε" : "User updated" });
        loadUsers();
      })
      .catch(() => setToast({ kind: "err", message: lang === "gr" ? "Σφάλμα ενημέρωσης χρήστη" : "Error updating user" }))
      .finally(() => setSavingUser(false));
  }
  function deleteEmployee(id) {
    setSavingUser(true);
    fetch(`${API_BASE}/users/${id}`, { method: "DELETE", headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(() => {
        setDeleteConfirm(false);
        setEditingId(null);
        setEditEmp(null);
        setToast({ kind: "ok", message: lang === "gr" ? "Ο χρήστης απενεργοποιήθηκε" : "User deactivated" });
        loadUsers();
      })
      .catch(() => setToast({ kind: "err", message: lang === "gr" ? "Σφάλμα ενημέρωσης χρήστη" : "Error updating user" }))
      .finally(() => setSavingUser(false));
  }
  function decideLeave(id,status,reviewNotes) { setLeaveReqs(prev=>prev.map(r=>r.id===id?{...r,status,review_notes:status==="rejected"?(reviewNotes||""):r.review_notes}:r)); }
  function confirmAdminReject() {
    if (!rejectLeaveModal) return;
    decideLeave(rejectLeaveModal.req.id, "rejected", rejectLeaveModal.notes);
    setRejectLeaveModal(null);
  }
  function saveHours() {
    if (!hoursStore || !storeHours) return;
    setHoursSaving(true);
    fetch(`${SHIFTIQ_API}/opening-hours`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        store_id: hoursStore,
        hours: storeHours
      })
    })
      .then(r => r.ok ? r.json() : null)
      .then(() => {
        setHoursSaved(true);
        setTimeout(() => setHoursSaved(false), 2500);
      })
      .catch(() => {})
      .finally(() => setHoursSaving(false));
  }
  function timeToMinsAdmin(t) {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  }
  function minsToTime(m) {
    const h = Math.floor(m / 60) % 24;
    const mm = m % 60;
    return String(h).padStart(2, "0") + ":" +
           String(mm).padStart(2, "0");
  }
  function stepTime(current, delta, min, max) {
    let m = timeToMinsAdmin(current) + delta;
    m = Math.max(min, Math.min(max, m));
    return minsToTime(m);
  }
  function toComparableCloseMins(open, close) {
    const openM = timeToMinsAdmin(open);
    let closeM = timeToMinsAdmin(close);
    if (closeM <= openM) closeM += 1440;
    return { openM, closeM };
  }

  const getEmp = id => employees.find(u=>u.id===id);
  const reqTypeLabel = type => {
    const meta = leaveRequestMeta(type, lang);
    return `${meta.icon} ${meta.adminLabel}`;
  };
  const storeOptions = storesWithAll;
  const displayed = employees.filter(u => {
    const ms = u.name.toLowerCase().includes(search.toLowerCase())||(u.email||"").toLowerCase().includes(search.toLowerCase())||((u.job_title||u.jobTitle||"").toLowerCase().includes(search.toLowerCase()));
    const mf = filterStore==="All Stores"||u.store===filterStore;
    return ms&&mf;
  });
  const platformDisplayed = platformUsers.filter(u => {
    const ms = u.name.toLowerCase().includes(search.toLowerCase())||(u.email||"").toLowerCase().includes(search.toLowerCase())||((u.job_title||u.jobTitle||"").toLowerCase().includes(search.toLowerCase()));
    const mf = filterStore==="All Stores"||u.store===filterStore || (u.store === "all" && filterStore === "All Stores");
    const ma = showInactive ? true : u.active !== false;
    return ms&&mf&&ma;
  });
  const pendingCount = leaveReqs.filter(r=>r.status==="pending").length;
  const byStore = storeNames.map(s=>({store:s,count:employees.filter(u=>u.store===s).length}));

  const TABS = [
    { id:"employees", label:lang==="gr"?"Υπάλληλοι":"Employees",   icon:"👥" },
    { id:"platform-users", label:lang==="gr"?"Χρήστες Πλατφόρμας":"Platform Users", icon:"👤" },
    { id:"leave",     label:lang==="gr"?"Άδειες":"Leave",          icon:"🌿" },
    { id:"requests",  label:lang==="gr"?"Αιτήματα":"Requests",     icon:"📋", badge:pendingCount },
    { id:"stores",    label:lang==="gr"?"Καταστήματα":"Stores",    icon:"🏪" },
    ...(SHIFTIQ_ENABLED && canAdmin
      ? [{ id: "headcount", label: lang === "gr" ? "Ανθρώπινο Δυναμικό" : "Headcount", icon: "📊" }]
      : []),
  ].filter((t) => !NAV_HIDDEN_PAGE_IDS.has(t.id));

  return (
    <div className="anim-fade">
      {toast && (
        <div style={{ position: "fixed", bottom: 18, left: "50%", transform: "translateX(-50%)", zIndex: 5000, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700, background: toast.kind === "err" ? "#FEF0ED" : "var(--surface)", border: `1px solid ${toast.kind === "err" ? "#FACFC8" : "var(--border)"}`, color: toast.kind === "err" ? "var(--danger)" : "var(--text-primary)" }}>
          {toast.message}
        </div>
      )}
      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:10,marginBottom:20}}>
        <div className="admin-stat-card"><div className="admin-stat-num">{employees.length}</div><div className="admin-stat-label">{lang==="gr"?"Σύνολο":"Total Staff"}</div></div>
        {byStore.map(s=><div key={s.store} className="admin-stat-card"><div className="admin-stat-num">{s.count}</div><div className="admin-stat-label">{s.store}</div></div>)}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,background:"var(--surface2)",padding:4,borderRadius:14,marginBottom:18,width:"fit-content",flexWrap:"wrap"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 18px",borderRadius:10,border:"none",cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"DM Sans,sans-serif",transition:"all 0.15s",background:tab===t.id?"var(--green)":"transparent",color:tab===t.id?"#fff":"var(--text-secondary)"}}>
            {t.icon} {t.label}
            {t.badge>0&&<span style={{background:"#EF4444",color:"#fff",fontSize:10,fontWeight:800,padding:"1px 6px",borderRadius:20}}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* ── EMPLOYEES TAB ── */}
      {tab==="employees"&&(
        <div>
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
            <div style={{position:"relative",flex:1,minWidth:180}}>
              <input className="admin-search" placeholder={lang==="gr"?"Αναζήτηση...":"Search name, email, title..."} value={search} onChange={e=>setSearch(e.target.value)} style={{width:"100%",maxWidth:"100%",paddingLeft:36}}/>
              <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",opacity:0.35}}><Icon name="search" size={14}/></span>
            </div>
            <div className="filter-row" style={{margin:0}}>
              {storeOptions.map(s=><button key={s} className={`filter-chip ${filterStore===s?"chip-active":""}`} onClick={()=>setFilter(s)}>{s==="All Stores"?(lang==="gr"?"Όλα":"All"):s}</button>)}
            </div>
          </div>
          <div style={{fontWeight:800,fontSize:15,color:"var(--text-primary)",marginBottom:10}}>
            {lang==="gr" ? "Μητρώο Προσωπικού" : "Staff Roster"}
          </div>
          {!isMobile?(
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr>
                  <th style={{minWidth:180}}>{lang==="gr"?"Υπάλληλος":"Employee"}</th>
                  <th style={{minWidth:180}}>Email</th>
                  <th style={{minWidth:150,whiteSpace:"nowrap"}}>{lang==="gr"?"Θέση":"Job Title"}</th>
                  <th style={{minWidth:150,whiteSpace:"nowrap"}}>{lang==="gr"?"Ομάδα":"Group"}</th>
                  <th style={{minWidth:90,whiteSpace:"nowrap"}}>{lang==="gr"?"Βάρδια":"Shift hrs"}</th>
                  <th style={{minWidth:80,whiteSpace:"nowrap"}}>{lang==="gr"?"Μέρες":"Days/wk"}</th>
                  <th style={{minWidth:130,whiteSpace:"nowrap"}}>{lang==="gr"?"Κατάστημα":"Store"}</th>
                  <th style={{minWidth:120,whiteSpace:"nowrap"}}>{lang==="gr"?"Ρόλος":"Role"}</th>
                </tr></thead>
                <tbody>
                  {displayed.map(emp=>(
                    <tr key={emp.id}>
                      <td><div style={{display:"flex",alignItems:"center",gap:10}}><div className="admin-avatar">{emp.name[0]}</div><div><div style={{fontWeight:700,color:"var(--text-primary)",fontSize:13}}>{emp.name}</div><div style={{fontSize:11,color:"var(--text-muted)",marginTop:1}}>{emp.phone ?? "—"}</div></div></div></td>
                      <td style={{color:"var(--text-secondary)",fontSize:12}}>{emp.email ?? "—"}</td>
                      <td>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15 }}>
                          <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"var(--surface2)",color:"var(--text-primary)",whiteSpace:"nowrap"}}>{emp.job_title ?? emp.jobTitle ?? "—"}</span>
                          {isKitchenStaffL(emp) && (
                            <span style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic", marginTop: 3 }}>
                              Λαντζέρης
                            </span>
                          )}
                        </div>
                      </td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{groupLabel(schedulingGroupFor(emp))}</span></td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{`${emp.shift_hours ?? emp.shiftHours ?? 8} hr`}</span></td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{`${emp.days_per_week ?? emp.workDays ?? 5} day`}</span></td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{emp.store}</span></td>
                      <td><span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"var(--green-light)",color:"var(--green)",whiteSpace:"nowrap"}}>{ROLES[emp.role]?.label||emp.role}</span></td>
                    </tr>
                  ))}
                  {displayed.length===0&&<tr><td colSpan={8} style={{textAlign:"center",padding:32,color:"var(--text-muted)",fontWeight:600}}>{lang==="gr"?"Δεν βρέθηκαν":"No results found"}</td></tr>}
                </tbody>
              </table>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {displayed.map(emp=>(
                <div key={emp.id} className="mobile-emp-card">
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                    <div className="admin-avatar">{emp.name[0]}</div>
                    <div style={{flex:1,minWidth:0}}><div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)"}}>{emp.name}</div><div style={{fontSize:11,color:"var(--text-muted)"}}>{emp.email ?? "—"}</div></div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {[{key:"store",label:lang==="gr"?"Κατάστημα":"Store",value:emp.store},{key:"shift",label:lang==="gr"?"Ώρες":"Shift",value:`${emp.shift_hours ?? emp.shiftHours ?? 8} hr`},{key:"days",label:lang==="gr"?"Μέρες/εβδ":"Days/wk",value:`${emp.days_per_week ?? emp.workDays ?? 5} day`},{key:"title",label:lang==="gr"?"Θέση":"Job Title",value:emp.job_title ?? emp.jobTitle ?? "—"},{key:"group",label:lang==="gr"?"Ομάδα":"Group",value:groupLabel(schedulingGroupFor(emp))},{key:"role",label:lang==="gr"?"Ρόλος":"Role",value:ROLES[emp.role]?.label || emp.role}].map(f=>(
                      <div key={f.key}>
                        <div style={{fontSize:9,fontWeight:800,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{f.label}</div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text-primary)"}}>{f.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      )}

      {tab==="platform-users"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
            <div style={{fontWeight:800,fontSize:15,color:"var(--text-primary)"}}>
              {lang==="gr" ? "Χρήστες Πλατφόρμας" : "Platform Users"}
            </div>
            {isFullAdmin && (
            <button className="btn-base btn-green" onClick={() => { setAddError(""); setShowAdd(true); }}>
              <Icon name="plus" size={14} />
              {lang === "gr" ? "Προσθήκη Χρήστη" : "Add User"}
            </button>
            )}
          </div>
          <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-muted)", maxWidth: 720 }}>
            {lang === "gr"
              ? "Θέση, ώρες και μέρες προέρχονται από Foxline. Για πλήρωμα με PRSN χρησιμοποιήστε Identity → πρόσκληση."
              : "Job title, hours, and work days come from Foxline. Invite crew with a PRSN from Identity."}
          </p>
          <label style={{display:"inline-flex",alignItems:"center",gap:8,marginBottom:10,fontSize:12,fontWeight:700,color:"var(--text-secondary)",cursor:"pointer"}}>
            <input type="checkbox" checked={showInactive} onChange={(e)=>setShowInactive(e.target.checked)} />
            {lang === "gr" ? "Εμφάνιση ανενεργών χρηστών" : "Show inactive users"}
          </label>
          {usersError && <div style={{marginBottom:10,color:"var(--danger)",fontSize:13,fontWeight:700}}>{usersError}</div>}
          {usersLoading ? (
            <div style={{textAlign:"center",padding:20}}><LoadingPulse size={18}/></div>
          ) : !isMobile ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr>
                  <th style={{minWidth:180}}>{lang==="gr"?"Υπάλληλος":"Employee"}</th>
                  <th style={{minWidth:180}}>Email</th>
                  <th style={{minWidth:150,whiteSpace:"nowrap"}}>{lang==="gr"?"Θέση":"Job Title"}</th>
                  <th style={{minWidth:150,whiteSpace:"nowrap"}}>{lang==="gr"?"Ομάδα":"Group"}</th>
                  <th style={{minWidth:90,whiteSpace:"nowrap"}}>{lang==="gr"?"Βάρδια":"Shift hrs"}</th>
                  <th style={{minWidth:80,whiteSpace:"nowrap"}}>{lang==="gr"?"Μέρες":"Days/wk"}</th>
                  <th style={{minWidth:130,whiteSpace:"nowrap"}}>{lang==="gr"?"Κατάστημα":"Store"}</th>
                  <th style={{minWidth:120,whiteSpace:"nowrap"}}>{lang==="gr"?"Ρόλος":"Role"}</th>
                  <th style={{minWidth:90}}></th>
                </tr></thead>
                <tbody>
                  {platformDisplayed.map(emp=>(
                    <tr key={emp.id} style={emp.active === false ? { opacity: 0.65 } : undefined}>
                      <td><div style={{display:"flex",alignItems:"center",gap:10}}><div className="admin-avatar">{emp.name[0]}</div><div><div style={{fontWeight:700,color:"var(--text-primary)",fontSize:13,display:"flex",alignItems:"center",gap:8}}>{emp.name}{(emp.role === "admin" || emp.role === "hr") && <NotificationBellIndicator enabled={emp.receive_notifications} lang={lang} />}{emp.active === false && <span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:999,background:"#E5E7EB",color:"#4B5563"}}>{lang==="gr"?"Ανενεργός":"Inactive"}</span>}</div><div style={{fontSize:11,color:"var(--text-muted)",marginTop:1}}>{emp.phone ?? "—"}</div></div></div></td>
                      <td style={{color:"var(--text-secondary)",fontSize:12}}>{emp.email ?? "—"}</td>
                      <td><span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"var(--surface2)",color:"var(--text-primary)",whiteSpace:"nowrap"}}>{emp.job_title ?? emp.jobTitle ?? "—"}</span></td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{groupLabel(emp.group_name ?? emp.group)}</span></td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{`${emp.shift_hours ?? emp.shiftHours ?? 8} hr`}</span></td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{`${emp.work_days ?? emp.days_per_week ?? emp.workDays ?? 5} day`}</span></td>
                      <td><span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap"}}>{emp.store}</span></td>
                      <td><span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"var(--green-light)",color:"var(--green)",whiteSpace:"nowrap"}}>{ROLES[emp.role]?.label||emp.role}</span></td>
                      <td style={{textAlign:"right"}}>
                        {isFullAdmin && (
                        <button className="btn-base btn-outline btn-small" onClick={() => { setEditingId(emp.id); setDeleteConfirm(false); }}>
                          <Icon name="edit" size={13}/>
                        </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {platformDisplayed.length===0&&<tr><td colSpan={9} style={{textAlign:"center",padding:32,color:"var(--text-muted)",fontWeight:600}}>{lang==="gr"?"Δεν βρέθηκαν":"No results found"}</td></tr>}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {platformDisplayed.map(emp=>(
                <div key={emp.id} className="mobile-emp-card" style={emp.active === false ? { opacity: 0.65 } : undefined}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                    <div className="admin-avatar">{emp.name[0]}</div>
                    <div style={{flex:1,minWidth:0}}><div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)",display:"flex",alignItems:"center",gap:8}}>{emp.name}{(emp.role === "admin" || emp.role === "hr") && <NotificationBellIndicator enabled={emp.receive_notifications} lang={lang} />}</div><div style={{fontSize:11,color:"var(--text-muted)"}}>{emp.email ?? "—"}</div></div>
                    {emp.active === false && <span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:999,background:"#E5E7EB",color:"#4B5563"}}>{lang==="gr"?"Ανενεργός":"Inactive"}</span>}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {[{key:"store",label:lang==="gr"?"Κατάστημα":"Store",value:emp.store},{key:"shift",label:lang==="gr"?"Ώρες":"Shift",value:`${emp.shift_hours ?? emp.shiftHours ?? 8} hr`},{key:"days",label:lang==="gr"?"Μέρες/εβδ":"Days/wk",value:`${emp.work_days ?? emp.days_per_week ?? emp.workDays ?? 5} day`},{key:"title",label:lang==="gr"?"Θέση":"Job Title",value:emp.job_title ?? emp.jobTitle ?? "—"},{key:"group",label:lang==="gr"?"Ομάδα":"Group",value:groupLabel(emp.group_name ?? emp.group)},{key:"role",label:lang==="gr"?"Ρόλος":"Role",value:ROLES[emp.role]?.label || emp.role}].map(f=>(
                      <div key={f.key}>
                        <div style={{fontSize:9,fontWeight:800,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{f.label}</div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text-primary)"}}>{f.value}</div>
                      </div>
                    ))}
                  </div>
                  {isFullAdmin && (
                  <button className="btn-base btn-outline btn-small" onClick={() => { setEditingId(emp.id); setDeleteConfirm(false); }} style={{ marginTop: 10 }}>
                    <Icon name="edit" size={13}/> {lang==="gr"?"Επεξεργασία":"Edit"}
                  </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── LEAVE TAB ── */}
      {tab==="leave" && !NAV_HIDDEN_PAGE_IDS.has("leave") && (
        <div>
          <div style={{background:"#F0FDF4",border:"1.5px solid #86EFAC",borderRadius:16,padding:"14px 18px",marginBottom:20}}>
            <div style={{fontWeight:800,fontSize:13,color:"#15803D",marginBottom:8}}>🌿 {lang==="gr"?"Πώς λειτουργεί η ετήσια άδεια":"How annual leave works in Greece"}</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {[{icon:"📅",en:"Year 1: Pro-rata ~1.67 days/month (5d/w) or 2 days/month (6d/w). Days unlock gradually.",gr:"Έτος 1: Αναλογικά ~1.67 μέρες/μήνα (5ήμερο). Οι μέρες ξεκλειδώνουν σταδιακά."},{icon:"📆",en:"Year 2: 21 days (5d/w) or 25 days (6d/w), taken in portions.",gr:"Έτος 2: 21 μέρες (5ήμερο) ή 25 μέρες (6ήμερο), τμηματικά."},{icon:"✅",en:"Year 3+: Full entitlement from January 1st. 22 days (5d/w) or 26 days (6d/w).",gr:"Έτος 3+: Πλήρης από 1η Ιανουαρίου. 22 μέρες (5ήμερο) ή 26 μέρες (6ήμερο)."},{icon:"⭐",en:"10yrs same employer OR 12yrs total: 25 days (5d/w) or 30 days (6d/w).",gr:"10χρ. ίδιος εργοδότης Ή 12χρ. συνολικά: 25 μέρες (5ήμερο) ή 30 μέρες (6ήμερο)."},{icon:"⏳",en:"Unused days expire Dec 31 (or Mar 31 next year if employer's fault).",gr:"Αχρησιμοποίητες μέρες λήγουν 31/12 (ή 31/3 επόμενου έτους αν φταίει ο εργοδότης)."}].map(item=>(
                <div key={item.en} style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                  <span style={{flexShrink:0}}>{item.icon}</span>
                  <span style={{fontSize:12,color:"#166534"}}>{lang==="gr"?item.gr:item.en}</span>
                </div>
              ))}
            </div>
          </div>
          {(() => {
            const priorYearsUnreviewedCount = employees.filter((rawEmp) => {
              const emp = mergeLeaveEmp(rawEmp);
              return !(emp.prior_years_reviewed ?? emp.priorYearsReviewed ?? false);
            }).length;
            if (priorYearsUnreviewedCount === 0) return null;
            return (
              <div style={{background:"#FFFBEB",border:"1.5px solid #FCD34D",borderRadius:14,padding:"12px 16px",marginBottom:16,fontSize:13,fontWeight:700,color:"#92400E"}}>
                {lang === "gr"
                  ? `${priorYearsUnreviewedCount} εργαζόμενοι χωρίς έλεγχο προϋπηρεσίας εκτός`
                  : `${priorYearsUnreviewedCount} employee${priorYearsUnreviewedCount === 1 ? "" : "s"} with prior years not reviewed`}
              </div>
            );
          })()}
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
            <div style={{position:"relative",flex:1,minWidth:180}}>
              <input
                className="admin-search"
                placeholder={lang==="gr"?"Αναζήτηση με όνομα ή κωδικό…":"Search by name or code…"}
                value={search}
                onChange={e=>setSearch(e.target.value)}
                style={{width:"100%",maxWidth:"100%",paddingLeft:36}}
              />
              <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",opacity:0.35}}><Icon name="search" size={14}/></span>
            </div>
            <div className="filter-row" style={{margin:0}}>
              {storeOptions.map(s=><button key={s} className={`filter-chip ${filterStore===s?"chip-active":""}`} onClick={()=>setFilter(s)}>{s==="All Stores"?(lang==="gr"?"Όλα":"All"):s}</button>)}
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {(() => {
              const leaveDisplayed = employees.filter((u) => {
                const ms = staffMatchesSearch(u, search);
                const mf = filterStore === "All Stores" || u.store === filterStore;
                return ms && mf;
              });
              if (leaveDisplayed.length === 0) {
                return (
                  <div style={{fontSize:13,color:"var(--text-muted)",fontWeight:600,padding:"12px 0"}}>
                    {lang === "gr" ? "Κανένα αποτέλεσμα" : "No matches"}
                  </div>
                );
              }
              return leaveDisplayed.map(rawEmp=>{
              const emp = mergeLeaveEmp(rawEmp);
              const hireDate = emp.hireDate ?? emp.hire_date;
              const yrsHere=(new Date()-new Date(hireDate))/(1000*60*60*24*365.25);
              const priorYearsRaw = emp.priorYears ?? emp.prior_years;
              const priorYears = priorYearsRaw || 0;
              const priorYearsReviewed = !!(emp.prior_years_reviewed ?? emp.priorYearsReviewed);
              const hireMs = hireDate ? new Date(hireDate).getTime() : NaN;
              const hireOlderThan30d = Number.isFinite(hireMs) && (Date.now() - hireMs) > 30 * 24 * 60 * 60 * 1000;
              const showPriorNotReviewed =
                !priorYearsReviewed &&
                (priorYearsRaw == null || Number(priorYearsRaw) === 0) &&
                hireOlderThan30d;
              const yrsTotal=yrsHere+priorYears;
              const wd=emp.workDays ?? emp.work_days ?? emp.days_per_week ?? 5;
              const code = String(emp.employee_code || userEmployeeCode(rawEmp) || "").trim();
              const bal = code ? adminLeaveBalancesByCode[code] : null;
              const available = bal != null ? bal.truly_available_days : (adminBalancesLoading ? "…" : "—");
              const taken = bal != null ? bal.used_days : (adminBalancesLoading ? "…" : "—");
              const lawDays = bal != null ? bal.tier_entitlement : (adminBalancesLoading ? "…" : "—");
              const futureBooked = bal?.future_booked_days ?? 0;
              const override=emp.leaveOverride ?? emp.leave_override ?? null;
              let tierText,tierColor,availNote;
              if(override!==null){tierText=lang==="gr"?"Χειροκίνητη ρύθμιση":"Manual override";tierColor="#B45309";availNote=lang==="gr"?"Ορίστηκε από HR":"Set by HR";}
              else if(yrsHere<1){const months=Math.min(yrsHere*12,12);const unlocked=Math.floor((wd===6?2:1.67)*months);tierText=lang==="gr"?"1ος χρόνος":"Year 1";tierColor="#B45309";availNote=lang==="gr"?`~${unlocked} μέρες διαθέσιμες τώρα`:`~${unlocked} days available now`;}
              else if(yrsHere<2){tierText=lang==="gr"?"2ος χρόνος":"Year 2";tierColor="#B45309";availNote=lang==="gr"?"Σε τμήματα":"In portions";}
              else if(yrsTotal>=25){tierText="25+ yrs total";tierColor="var(--green)";availNote=lang==="gr"?"Πλήρης από 1 Ιαν.":"Full from Jan 1";}
              else if(yrsHere>=10||yrsTotal>=12){tierText=yrsHere>=10?"10+ yrs here":"12+ yrs total";tierColor="var(--green)";availNote=lang==="gr"?"Πλήρης από 1 Ιαν.":"Full from Jan 1";}
              else{tierText=lang==="gr"?"3ος χρόνος+":"Year 3+";tierColor="var(--green)";availNote=lang==="gr"?"Πλήρης από 1 Ιαν.":"Full from Jan 1";}
              return (
                <div key={emp.id} style={{background:"var(--surface)",border:"1.5px solid var(--border)",borderRadius:18,padding:"16px 20px",boxShadow:"var(--shadow)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                    <div className="admin-avatar">{emp.name[0]}</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:14,color:"var(--text-primary)"}}>{emp.name}</div>
                      {code ? (
                        <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2,fontWeight:600}}>{code}</div>
                      ) : null}
                      <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{emp.job_title ?? emp.jobTitle} · {emp.store} · {wd}d/wk · {lang==="gr"?"Πρόσληψη":"Hired"} {hireDate}</div>
                    </div>
                    <div style={{textAlign:"center",background:"var(--green-light)",borderRadius:14,padding:"10px 18px"}}>
                      <div style={{fontFamily:"Bebas Neue,sans-serif",fontSize:36,color:"var(--green)",lineHeight:1}}>{available}</div>
                      <div style={{fontSize:10,fontWeight:700,color:"var(--green)",marginTop:2}}>{lang==="gr"?"διαθέσιμες":"days available"}</div>
                      <div style={{fontSize:10,color:"var(--text-muted)",marginTop:4,fontWeight:600}}>{taken} {lang==="gr"?"μέρες ληφθείσες":"days taken"}</div>
                      {bal != null && futureBooked > 0 && (
                        <div style={{fontSize:10,color:"var(--text-muted)",marginTop:4,fontWeight:600,lineHeight:1.35}}>
                          {lang === "gr"
                            ? `${futureBooked} ήδη κλεισμένες εκ των μήν`
                            : `${futureBooked} already booked ahead`}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
                    <div style={{background:"var(--surface2)",borderRadius:12,padding:"10px 14px"}}>
                      <div style={{fontSize:10,fontWeight:800,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{lang==="gr"?"Κατηγορία":"Seniority"}</div>
                      <div style={{fontWeight:800,fontSize:13,color:tierColor}}>{tierText}</div>
                      <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{availNote}</div>
                    </div>
                    <div style={{background:"var(--surface2)",borderRadius:12,padding:"10px 14px"}}>
                      <div style={{fontSize:10,fontWeight:800,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{lang==="gr"?"Νόμος (αυτόματο)":"Law (auto)"}</div>
                      <div style={{fontWeight:800,fontSize:20,color:override!==null?"var(--text-muted)":"var(--green)",textDecoration:override!==null?"line-through":"none"}}>{lawDays}d</div>
                    </div>
                    <div style={{background:"var(--surface2)",borderRadius:12,padding:"10px 14px"}}>
                      <div style={{fontSize:10,fontWeight:800,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{lang==="gr"?"Προηγ. εργοδότης (χρόνια)":"Prior employer (yrs)"}</div>
                      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                        <input type="number" min={0} max={40} value={(emp.priorYears ?? emp.prior_years) || 0} onChange={e=>updateLeaveConfig(rawEmp,{prior_years:parseInt(e.target.value,10)||0})} style={{width:56,background:"var(--surface)",border:"1.5px solid var(--border)",borderRadius:8,color:"var(--text-primary)",padding:"5px 8px",fontSize:14,fontWeight:800,fontFamily:"DM Sans,sans-serif"}}/>
                        <span style={{fontSize:11,color:"var(--text-muted)"}}>{lang==="gr"?"χρόνια":"yrs"}</span>
                        {showPriorNotReviewed && (
                          <span style={{fontSize:11,fontWeight:700,color:"#B45309"}}>
                            {lang === "gr" ? "(μη ελεγμένο)" : "(not reviewed)"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{background:"var(--surface2)",borderRadius:12,padding:"10px 14px"}}>
                      <div style={{fontSize:10,fontWeight:800,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{lang==="gr"?"Μεταφορά από πέρσι":"Carry-over"}</div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <input type="number" min={0} max={30} value={(emp.carryOver ?? emp.carry_over) || 0} onChange={e=>updateLeaveConfig(rawEmp,{carry_over:parseInt(e.target.value,10)||0})} style={{width:56,background:"var(--surface)",border:"1.5px solid var(--border)",borderRadius:8,color:"var(--text-primary)",padding:"5px 8px",fontSize:14,fontWeight:800,fontFamily:"DM Sans,sans-serif"}}/>
                        <span style={{fontSize:11,color:"var(--text-muted)"}}>{lang==="gr"?"μέρες (λήγουν 31/3)":"days (exp. Mar 31)"}</span>
                      </div>
                    </div>
                    <div style={{background:override!==null?"#FFFBEB":"var(--surface2)",border:override!==null?"1.5px solid #FCD34D":"1.5px solid transparent",borderRadius:12,padding:"10px 14px"}}>
                      <div style={{fontSize:10,fontWeight:800,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{lang==="gr"?"HR Override":"HR Override"}</div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <input type="number" min={0} max={50} placeholder={lang==="gr"?"αυτόματο":"auto"} value={override??""} onChange={e=>updateLeaveConfig(rawEmp,{leave_override:e.target.value===""?null:parseInt(e.target.value,10)})} style={{width:56,background:"var(--surface)",border:override!==null?"1.5px solid #FCD34D":"1.5px solid var(--border)",borderRadius:8,color:"var(--text-primary)",padding:"5px 8px",fontSize:14,fontWeight:800,fontFamily:"DM Sans,sans-serif"}}/>
                        {override!==null&&<button onClick={()=>updateLeaveConfig(rawEmp,{leave_override:null})} style={{border:"none",background:"none",cursor:"pointer",color:"var(--text-muted)",fontSize:14}}>✕</button>}
                      </div>
                      <div style={{fontSize:10,color:"#B45309",marginTop:4}}>{override!==null?(lang==="gr"?"Αντικαθιστά τον αυτόματο":"Overrides auto-calc"):(lang==="gr"?"Κενό = αυτόματο":"Blank = auto")}</div>
                    </div>
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </div>
      )}

      {/* ── REQUESTS TAB ── */}
      {tab==="requests"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {leaveReqs.length===0&&<div className="empty-state"><Icon name="leaf" size={40}/><p>{lang==="gr"?"Δεν υπάρχουν αιτήματα":"No requests"}</p></div>}
          {leaveReqs.map(req=>{
            const emp=getEmp(req.employeeId);
            const meta = leaveRequestMeta(req.type, lang);
            return (
              <div key={req.id} style={{background:"var(--surface)",border:"1.5px solid var(--border)",borderLeft:req.overdue?"4px solid #F59E0B":"1.5px solid var(--border)",borderRadius:16,padding:"16px 18px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",boxShadow:"var(--shadow)"}}>
                <div style={{width:42,height:42,background:meta.bg,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                  {meta.icon}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:800,fontSize:14,color:"var(--text-primary)"}}>{reqTypeLabel(req.type)}</div>
                  {req.shift_preference&&<div style={{fontSize:12,color:"var(--text-muted)",marginTop:2,fontWeight:600}}>{req.shift_preference}</div>}
                  {req.overdue&&<div style={{fontSize:11,color:"#B45309",marginTop:3,fontWeight:800}}>{lang==="gr"?"Αναμένει απάντηση >7 ημέρες":"Awaiting response >7 days"}</div>}
                  {emp?<div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}><div className="admin-avatar" style={{width:20,height:20,fontSize:9}}>{emp.name[0]}</div><span style={{fontSize:12,fontWeight:600,color:"var(--text-secondary)"}}>{emp.name}</span><span style={{fontSize:11,color:"var(--text-muted)"}}>· {emp.store}</span></div>:req.employee_name&&<div style={{fontSize:12,fontWeight:600,color:"var(--text-secondary)",marginTop:3}}>{req.employee_name}{req.store_id ? ` · ${req.store_id}` : ""}</div>}
                  <div style={{fontSize:12,color:"var(--text-muted)",marginTop:4}}>📅 {req.date}{req.reason?` · ${req.reason}`:""}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                  <span style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:800,textTransform:"uppercase",...({pending:{background:"#FEF3C7",color:"#B45309"},approved:{background:"#DCFCE7",color:"#16A34A"},rejected:{background:"#FEF0ED",color:"var(--danger)"}}[req.status])}}>
                    {req.status}
                  </span>
                  {req.status==="rejected"&&req.review_notes&&<div style={{fontSize:11,color:"var(--text-muted)",fontWeight:600,maxWidth:220,textAlign:"right"}}>{lang==="gr"?"Αιτία":"Reason"}: {req.review_notes}</div>}
                </div>
                {req.status==="pending"&&(
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>decideLeave(req.id,"approved")} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",background:"var(--green)",color:"#fff",border:"none",borderRadius:10,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"DM Sans,sans-serif"}}><Icon name="check" size={13}/>{lang==="gr"?"Έγκριση":"Approve"}</button>
                    <button onClick={()=>setRejectLeaveModal({req,notes:""})} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",background:"#FEF0ED",color:"var(--danger)",border:"1.5px solid #FACFC8",borderRadius:10,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"DM Sans,sans-serif"}}><Icon name="close" size={13}/>{lang==="gr"?"Απόρριψη":"Reject"}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {rejectLeaveModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setRejectLeaveModal(null)}>
          <div className="modal-box" style={{maxWidth:420}}>
            <div className="modal-handle"/>
            <div className="modal-title">{lang==="gr"?"Απόρριψη Αιτήματος":"Reject Request"}</div>
            <div className="form-group">
              <label className="form-label">{lang==="gr"?"Αιτία απόρριψης (προαιρετικό)":"Reason for rejection (optional)"}</label>
              <textarea
                className="form-input"
                rows={3}
                value={rejectLeaveModal.notes}
                onChange={e=>setRejectLeaveModal({...rejectLeaveModal,notes:e.target.value})}
                placeholder={lang==="gr"?"π.χ. Δεν υπάρχει διαθεσιμότητα εκείνη την εβδομάδα":"e.g. Already too many staff off that week"}
                style={{resize:"vertical",minHeight:86}}
              />
            </div>
            <div style={{display:"flex",gap:9}}>
              <button className="btn-base btn-danger-soft" style={{flex:1,justifyContent:"center"}} onClick={confirmAdminReject}>{lang==="gr"?"Επιβεβαίωση Απόρριψης":"Confirm Rejection"}</button>
              <button className="btn-base btn-outline" onClick={()=>setRejectLeaveModal(null)}>{lang==="gr"?"Ακύρωση":"Cancel"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── STORES TAB ── */}
      {tab==="stores"&&(
        <div>
          <div style={{fontWeight:800,fontSize:15,color:"var(--text-primary)",marginBottom:6}}>{lang==="gr"?"Καταστήματα":"Stores"}</div>
          <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:18}}>{lang==="gr"?"Λίστα από το σύστημα (admin_unified_locations). Επιλέξτε κατάστημα για το ωράριο λειτουργίας.":"Store list is loaded from the central location master. Select a store to edit opening hours."}</div>
          <div style={{display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap"}}>
            <div style={{flex:"0 1 360px",minWidth:260,maxWidth:420,display:"flex",flexDirection:"column",gap:8}}>
              {storeNames.map(s=>{
                const selected = hoursStore === s;
                const count = employees.filter(u=>u.store===s).length;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setHoursStore(s);
                      setFilter(s);
                    }}
                    style={{
                      display:"flex",
                      alignItems:"center",
                      justifyContent:"space-between",
                      background: selected ? "var(--green-light)" : "var(--surface)",
                      border: `1.5px solid ${selected ? "var(--green)" : "var(--border)"}`,
                      borderRadius:12,
                      padding:"12px 16px",
                      cursor:"pointer",
                      textAlign:"left",
                      fontFamily:"DM Sans,sans-serif",
                      width:"100%",
                    }}
                  >
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:18}} aria-hidden>🏪</span>
                      <div>
                        <div style={{fontWeight:700,fontSize:14,color: selected ? "var(--green)" : "var(--text-primary)"}}>{s}</div>
                        <div style={{fontSize:11,color:"var(--text-muted)"}}>{count} {lang==="gr"?"εργαζόμενοι":"employees"}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {canEditStoreSchedule && (
              <div style={{ flex:"1 1 320px", minWidth:280, maxWidth:520, background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 16 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text-primary)", marginBottom: 4 }}>
                  {lang==="gr"?"Ωράριο λειτουργίας":"Opening Hours"}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, fontWeight: 600 }}>
                  {hoursStore || (lang==="gr"?"Επιλέξτε κατάστημα":"Select a store")}
                </div>

                {!hoursStore ? (
                  <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "24px 0" }}>
                    {lang==="gr"?"Επιλέξτε κατάστημα από τη λίστα.":"Pick a store from the list."}
                  </div>
                ) : !storeHours ? (
                  <LoadingPulse size={20} />
                ) : (
                  <>
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => {
                      const dayValue = storeHours[day] || (STORE_DEFAULT_HOURS[hoursStore] || {})[day] || "CLOSED";
                      const isClosed = dayValue === "CLOSED";
                      const [open, close] = isClosed ? ["CLOSED", "CLOSED"] : dayValue.split("-");
                      const stepperBtnStyle = {
                        width: 28, height: 28, borderRadius: 8,
                        border: "1.5px solid var(--border)",
                        background: "var(--surface2)", cursor: isClosed ? "default" : "pointer",
                        fontSize: 14, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "DM Sans,sans-serif",
                        opacity: isClosed ? 0.4 : 1,
                      };
                      const timeStyle = {
                        width: 52, textAlign: "center", fontSize: 14,
                        fontWeight: 700, color: "var(--text-primary)"
                      };
                      return (
                        <div key={day} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                          <div style={{ width: 40, fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{day}</div>
                          <button
                            type="button"
                            style={stepperBtnStyle}
                            disabled={isClosed}
                            onClick={() => {
                              if (isClosed) return;
                              const newOpen = stepTime(open, -30, 360, 840);
                              const { openM, closeM } = toComparableCloseMins(newOpen, close);
                              if (closeM > openM) {
                                setStoreHours({ ...storeHours, [day]: newOpen + "-" + close });
                              }
                            }}
                          >
                            ◀
                          </button>
                          <div style={timeStyle}>{open}</div>
                          <button
                            type="button"
                            style={stepperBtnStyle}
                            disabled={isClosed}
                            onClick={() => {
                              if (isClosed) return;
                              const newOpen = stepTime(open, 30, 360, 840);
                              const { openM, closeM } = toComparableCloseMins(newOpen, close);
                              if (closeM > openM) {
                                setStoreHours({ ...storeHours, [day]: newOpen + "-" + close });
                              }
                            }}
                          >
                            ▶
                          </button>
                          <div style={{ fontWeight: 700, color: "var(--text-muted)" }}>–</div>
                          <button
                            type="button"
                            style={stepperBtnStyle}
                            disabled={isClosed}
                            onClick={() => {
                              if (isClosed) return;
                              const newClose = stepTime(close, -30, 840, 1560);
                              const { openM, closeM } = toComparableCloseMins(open, newClose);
                              if (closeM > openM) {
                                setStoreHours({ ...storeHours, [day]: open + "-" + newClose });
                              }
                            }}
                          >
                            ◀
                          </button>
                          <div style={timeStyle}>{close}</div>
                          <button
                            type="button"
                            style={stepperBtnStyle}
                            disabled={isClosed}
                            onClick={() => {
                              if (isClosed) return;
                              const newClose = stepTime(close, 30, 840, 1560);
                              const { openM, closeM } = toComparableCloseMins(open, newClose);
                              if (closeM > openM) {
                                setStoreHours({ ...storeHours, [day]: open + "-" + newClose });
                              }
                            }}
                          >
                            ▶
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      className="btn-base btn-green"
                      onClick={saveHours}
                      style={{ marginTop: 8, minWidth: 130, justifyContent: "center" }}
                    >
                      {hoursSaving ? (
                        <>
                          <span className="spinner" />
                          Saving...
                        </>
                      ) : hoursSaved ? "✓ Saved" : (lang==="gr"?"Αποθήκευση":"Save Hours")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {SHIFTIQ_ENABLED && tab === "headcount" && !isMobile && (
        <HeadcountPlanningPage user={user} lang={lang} />
      )}

      {/* ── EDIT EMPLOYEE MODAL ── */}
      {isFullAdmin && editingEmp && editEmp && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setEditingId(null)}>
          <div className="modal-box" style={{maxWidth:520}}>
            <div className="modal-handle"/>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
              <div className="admin-avatar" style={{width:44,height:44,fontSize:18}}>{editingEmp.name[0]}</div>
              <div><div style={{fontWeight:800,fontSize:16,color:"var(--text-primary)"}}>{editingEmp.name}</div><div style={{fontSize:12,color:"var(--text-muted)"}}>{editingEmp.email}</div></div>
              <button onClick={()=>setEditingId(null)} style={{marginLeft:"auto",border:"none",background:"var(--surface2)",borderRadius:8,width:30,height:30,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><Icon name="close" size={14}/></button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[{label:"Name",labelGr:"Όνομα",field:"name",type:"text",value:editEmp.name,required:true,full:true},{label:"Email",labelGr:"Email",field:"email",type:"email",value:editEmp.email,required:true,full:true},{label:"Password",labelGr:"Κωδικός",field:"password",type:"text",value:editEmp.password,full:true,ph:lang==="gr"?"Αφήστε κενό για μη αλλαγή":"Leave blank to keep unchanged"}].map(f=>(
                <div key={f.field} style={{gridColumn:f.full?"1/-1":"auto"}}>
                  <label className="form-label">{lang==="gr"?f.labelGr:f.label}{f.required ? " *" : ""}</label>
                  <input type={f.type} className="form-input" value={f.value ?? ""} placeholder={f.ph} onChange={e=>setEditEmp(prev=>({...prev,[f.field]:e.target.value}))}/>
                </div>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:12}}>
              {[
                {label:"Store",labelGr:"Κατάστημα",field:"store",opts:(editEmp.role === "admin" || editEmp.role === "brand_manager") ? [{ v:"All Stores", l: lang==="gr" ? "Όλα τα καταστήματα" : "All Stores" }] : [...(editEmp.role === "hr" ? [{ v:"all", l: lang==="gr" ? "Όλα τα καταστήματα" : "All Stores" }] : []), ...storeNames.map(s=>({v:s,l:s}))]},
                {label:"Role",labelGr:"Ρόλος",field:"role",opts:PLATFORM_USER_ROLE_OPTS},
                ...(editEmp.role === "brand_manager" ? [{label:"Brand",labelGr:"Brand",field:"brand_id",opts:brandOptions}] : []),
              ].map(f=>(
                <div key={f.field}>
                  <label className="form-label">{lang==="gr"?f.labelGr:f.label}</label>
                  <select
                    className="form-select"
                    value={editEmp[f.field] ?? ""}
                    disabled={f.field === "store" && (editEmp.role === "admin" || editEmp.role === "brand_manager")}
                    style={f.field === "store" && (editEmp.role === "admin" || editEmp.role === "brand_manager") ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                    onChange={e=>{
                      const raw = e.target.value;
                      const nextVal = ["shift_hours","work_days","brand_id"].includes(f.field) ? parseInt(raw, 10) : raw;
                      setEditEmp(prev => {
                        const next = { ...prev, [f.field]: nextVal };
                        if (f.field === "role") {
                          if (nextVal === "hr") { next.store = "all"; next.brand_id = null; }
                          else if (nextVal === "admin") { next.store = "All Stores"; next.brand_id = null; }
                          else if (nextVal === "brand_manager") { next.store = "All Stores"; next.brand_id = next.brand_id || pickDefaultBrandId(); }
                          else {
                            next.brand_id = null;
                            if (next.store === "all" || next.store === "All Stores") next.store = pickDefaultStore();
                          }
                        }
                        return next;
                      });
                    }}
                  >
                    {f.opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{marginTop:12,padding:"10px 12px",borderRadius:10,background:"var(--surface2)",fontSize:12,color:"var(--text-muted)",lineHeight:1.45}}>
              <div style={{fontWeight:700,color:"var(--text-primary)",marginBottom:4}}>{lang==="gr"?"Foxline (μόνο ανάγνωση)":"Foxline (read-only)"}</div>
              {(editEmp.job_title || editEmp.jobTitle || editEmp.shift_hours || editEmp.shiftHours || editEmp.work_days || editEmp.workDays)
                ? `${editEmp.job_title || editEmp.jobTitle || "—"} · ${editEmp.shift_hours ?? editEmp.shiftHours ?? "—"}h · ${editEmp.work_days ?? editEmp.workDays ?? "—"}d/wk`
                : (lang==="gr"?"Δεν υπάρχει σύνδεση Foxline — προσκαλέστε από Identity.":"No Foxline link — invite from Identity.")}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:20}}>
              {editingEmp.active !== false && deleteConfirm ? (
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:700,color:"var(--danger)"}}>{lang==="gr"?"Ο χρήστης θα απενεργοποιηθεί και δεν θα μπορεί να συνδεθεί. Συνέχεια;":"This will deactivate the user. They will no longer be able to log in. Continue?"}</span>
                  <button onClick={()=>deleteEmployee(editingEmp.id)} style={{padding:"6px 14px",background:"var(--danger)",color:"#fff",border:"none",borderRadius:9,fontWeight:700,fontSize:12,cursor:"pointer"}}>{lang==="gr"?"Ναι":"Confirm"}</button>
                  <button onClick={()=>setDeleteConfirm(false)} className="btn-base btn-outline btn-small">{lang==="gr"?"Όχι":"Cancel"}</button>
                </div>
              ) : editingEmp.active !== false ? (
                <button onClick={()=>setDeleteConfirm(true)} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"transparent",color:"var(--danger)",border:"1.5px solid #FACFC8",borderRadius:10,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"DM Sans,sans-serif"}}><Icon name="close" size={13}/>{lang==="gr"?"Απενεργοποίηση Χρήστη":"Deactivate User"}</button>
              ) : <span />
              }
              <button className="btn-base btn-green" onClick={saveEditUser} disabled={savingUser}>
                {savingUser ? (lang==="gr"?"Αποθήκευση...":"Saving...") : (lang==="gr"?"Αποθήκευση":"Save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD EMPLOYEE MODAL ── */}
      {isFullAdmin && showAdd&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAdd(false)}>
          <div className="modal-box" style={{maxWidth:540}}>
            <div className="modal-handle"/>
            <div className="modal-title">{lang==="gr"?"Προσθήκη Χρήστη":"Add User"}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[
                {label:"Name",labelGr:"Όνομα",field:"name",type:"text",required:true,full:true},
                {label:"Email",labelGr:"Email",field:"email",type:"email",required:true,full:true},
                {label:"Password",labelGr:"Κωδικός",field:"password",type:"text",required:true,full:true,ph:lang==="gr"?"Τουλάχιστον 8 χαρακτήρες":"At least 8 characters"},
                {label:"Role",labelGr:"Ρόλος",field:"role",type:"select",opts:PLATFORM_USER_ROLE_OPTS},
                {label:"Store",labelGr:"Κατάστημα",field:"store",type:"select",opts:(newEmp.role==="admin"||newEmp.role==="brand_manager")?[{v:"All Stores",l:lang==="gr"?"Όλα τα καταστήματα":"All Stores"}]:[...(newEmp.role==="hr"?[{v:"all",l:lang==="gr"?"Όλα τα καταστήματα":"All Stores"}]:[]),...storeNames.map(s=>({v:s,l:s}))]},
                ...(newEmp.role === "brand_manager" ? [{label:"Brand",labelGr:"Brand",field:"brand_id",type:"select",opts:brandOptions,required:true}] : []),
              ].map(f=>(
                <div key={f.field} className="form-group" style={{marginBottom:0,gridColumn:f.full?"1/-1":"auto"}}>
                  <label className="form-label">{lang==="gr"?f.labelGr:f.label}{f.required ? " *" : ""}</label>
                  {f.type==="select" ? (
                    <select className="form-select" value={newEmp[f.field] ?? ""} disabled={f.field==="store"&&(newEmp.role==="admin"||newEmp.role==="brand_manager")} style={f.field==="store"&&(newEmp.role==="admin"||newEmp.role==="brand_manager")?{opacity:0.55,cursor:"not-allowed"}:undefined} onChange={e=>{const v=e.target.value; if (f.field==="role") { let store=newEmp.store; let brand_id=null; if (v==="hr") store="all"; else if (v==="admin"||v==="brand_manager") store="All Stores"; else if (newEmp.store==="all"||newEmp.store==="All Stores") store=pickDefaultStore(); if (v==="brand_manager") brand_id=pickDefaultBrandId(); setNewEmp({...newEmp, role:v, store, brand_id}); return; } if (f.field==="brand_id") { setNewEmp({...newEmp, brand_id: Number(v)}); return; } setNewEmp({...newEmp,[f.field]:v});}}>
                      {f.opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} className="form-input" value={newEmp[f.field] ?? ""} placeholder={f.ph || ""} minLength={f.field==="password"?8:undefined} onChange={e=>setNewEmp({...newEmp,[f.field]:f.type==="number"?Number(e.target.value):e.target.value})}/>
                  )}
                </div>
              ))}
            </div>
            <p style={{marginTop:12,fontSize:12,color:"var(--text-muted)",lineHeight:1.45}}>
              {lang==="gr"
                ? "Θέση / ώρες / μέρες έρχονται από Foxline. Για πλήρωμα με PRSN: Identity → πρόσκληση."
                : "Job title / hours / days come from Foxline. For crew with a PRSN: Identity → invite."}
            </p>
            {addError&&<div style={{marginTop:12,background:"#FEF0ED",border:"1px solid #FACFC8",borderRadius:10,padding:"10px 14px",fontSize:13,color:"var(--danger)",fontWeight:600}}>{addError}</div>}
            <div style={{display:"flex",gap:9,marginTop:20}}>
              <button className="btn-base btn-green" style={{flex:1,justifyContent:"center"}} onClick={addEmployee} disabled={savingUser}><Icon name="plus" size={14}/>{savingUser ? (lang==="gr"?"Δημιουργία...":"Creating...") : (lang==="gr"?"Δημιουργία Χρήστη":"Create User")}</button>
              <button className="btn-base btn-outline" onClick={()=>{setShowAdd(false);setNewEmp(emptyNew);setAddError("");}}>{lang==="gr"?"Ακύρωση":"Cancel"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

